import { store } from "../store";

import { searchApi } from "./apiSingleton";

export interface Cabin {
  location: string;
  maximum_occupancy: number;
  minimum_occupancy: number;
  number: string;
  position: string;
  size: string;
}

type ICabinsResponse = Cabin[];

interface ICabinsRequest {
  cruiseId: string;
  rateCode: string;
  gradeCode: string;
  guestCount: string;
  guestsDobQs: string;
  source: string;
  language: string;
}

interface IHoldCabinResponse {
  cabin_number: string;
  held_at: string;
  held_until: string;
}

interface IHoldCabinRequest {
  cabin_number: string;
  grade_code: string;
  rate_code: string;
  sailing_code: string;
  agency: string;
}

interface ICreateReservationRequest {
  rate_code: string;
  cruiseId: string;
  residency?: string;
  source: string;
  language: string;
  cabins: Array<{
    cabin_number: string;
    dining_seating: string;
    dining_table: string;
    grade_code: string;
    guests: Array<{
      address: string[];
      city: string;
      country: string;
      email: string;
      given_name: string;
      language: string;
      lastname: string;
      last_name: string;
      lead_passenger: boolean;
      nationality: string;
      phone: string;
      state: string;
      title: string;
      gender: string;
      zip_code: string;
    }>;
  }>;
}

interface IGetPricingResponse {
  sailing: {
    sailing_code: string;
    cruise: {
      code: string;
      name: string;
    };
  };
  ship: { code: string; name: string };
  rate: { code: string; name: string };
  currency: string;
  total_price: string;
  guests: Array<{
    guest: number;
    total_price: string;
    total_fare: string;
    gft: string;
    nccf: string;
    discount: string;
  }>;
  payment_schedule: Array<{ date: string; currency: string; amount: string }>;
  cancellation_policy: Array<{ days_before_departure: number; policy: string }>;
  raw_cancellation_policy: Array<{
    price_type: string;
    type: string;
    value: number;
    days_before_departure: number;
  }>;
}

interface IGetPricingRequest {
  sailing_code: string;
  source: string;
  rate_code: string;
  grade_code: string;
  cabin_number: string;
  guest_count: string;
  guests_dobs: string;
  language: string;
  residency?: string;
}

export const CabinSelectAPI = searchApi.injectEndpoints({
  endpoints: (builder) => ({
    initCabins: builder.query<ICabinsResponse, ICabinsRequest>({
      query: ({
        cruiseId,
        rateCode,
        gradeCode,
        guestCount,
        guestsDobQs,
        source,
        language,
      }) => {
        const { sessionKey } = store.getState().session;

        return {
          url: `/cabins/${cruiseId}?session=${
            sessionKey ?? ""
          }&language=${language}&source=${source}&rate_code=${rateCode}&grade_code=${gradeCode}&guest_count=${guestCount}&${guestsDobQs}`,
        };
      },
    }),
    holdCabin: builder.query<IHoldCabinResponse, IHoldCabinRequest>({
      query: ({
        cabin_number,
        grade_code,
        rate_code,
        sailing_code,
        agency,
      }) => {
        const { sessionKey: session } = store.getState().session;

        return {
          url: `/holdcabin/${sailing_code}/${agency}/`,
          method: "POST",
          body: {
            cabin_number,
            grade_code,
            rate_code,
            sailing_code,
            session,
          },
        };
      },
    }),
    createReservation: builder.query<
      { pnr: string },
      ICreateReservationRequest
    >({
      query: ({ rate_code, cruiseId, source, cabins, language, residency }) => {
        const { sessionKey: session } = store.getState().session;
        const agency = 1;

        const body = {
          agency,
          cabins,
          language,
          promotions: [],
          rate_code,
          session,
          residency,
        };

        if (!body.residency) {
          delete body?.residency;
        }

        return {
          url: `/reservation/${cruiseId}/${source}/`,
          method: "POST",
          body,
        };
      },
    }),
    getPricing: builder.query<IGetPricingResponse, IGetPricingRequest>({
      query: ({
        sailing_code,
        source,
        rate_code,
        grade_code,
        cabin_number,
        guest_count,
        guests_dobs,
        language,
        residency,
      }) => {
        const { sessionKey } = store.getState().session;

        return {
          url: `/pricing/${sailing_code}/?sailing_code=${sailing_code}&session=${sessionKey}&source=${source}&language=${language}&rate_code=${rate_code}&grade_code=${grade_code}&cabin_number=${cabin_number}&guest_count=${guest_count}&${guests_dobs}${residency && `&residency=${residency}`}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useLazyInitCabinsQuery,
  useLazyHoldCabinQuery,
  useLazyCreateReservationQuery,
  useLazyGetPricingQuery,
} = CabinSelectAPI;
